import { createEmptyClip, normalizeClip } from './clip';
import { playClip } from './player';

const LIBRARY_KEY = 'optcg.animation.library';
const LEGACY_DRAFT_KEY = 'optcg.animation.editorDraft';
const LEGACY_OVERRIDE_KEY = 'optcg.animation.rarityFlourish';

const prefabs = new Map();
const clipCache = new Map();

export const CLIP_NAME = /^[A-Za-z][A-Za-z0-9-]*$/;

const storage = () => (typeof localStorage === 'undefined' ? null : localStorage);

const parseJson = (raw) => {
    try {
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        return null;
    }
};

// One-time lift of the old single-clip editor into packOpen/flourish.
const readLibrary = () => {
    const store = storage();
    const library = parseJson(store?.getItem(LIBRARY_KEY)) || {};
    if (!store || library.packOpen?.flourish) return library;

    const legacy = parseJson(store.getItem(LEGACY_DRAFT_KEY)) || parseJson(store.getItem(LEGACY_OVERRIDE_KEY));
    if (!legacy) return library;

    library.packOpen = { ...library.packOpen, flourish: legacy };
    try {
        store.setItem(LIBRARY_KEY, JSON.stringify(library));
        store.removeItem(LEGACY_DRAFT_KEY);
        store.removeItem(LEGACY_OVERRIDE_KEY);
    } catch (error) {
        delete library.packOpen.flourish;
    }
    return library;
};

const writeLibrary = (library) => {
    const store = storage();
    if (!store) return;
    store.setItem(LIBRARY_KEY, JSON.stringify(library));
};

// `create` builds the editor preview for this clip. Without one, the prefab's create is used.
export const stubClip = (name, load, create) => ({
    name: String(name),
    load,
    ...(typeof create === 'function' ? { create } : {}),
});

export const clipCreate = (prefab, clipName) => {
    const stub = prefab?.clips?.find((clip) => clip.name === clipName);
    return stub?.create || prefab?.create;
};

export const definePrefab = ({ id, name, load, create, parts, clips }) => {
    const key = String(id || '').trim();
    if (!key) return null;

    const prefab = {
        id: key,
        name: String(name || key),
        load: load || (async () => {}),
        create,
        parts: parts || {},
        clips: Array.isArray(clips) ? clips : [],
    };
    prefabs.set(key, prefab);
    return prefab;
};

export const listPrefabs = () => [...prefabs.values()];

export const getPrefab = (id) => prefabs.get(id) || null;

export const listParts = (prefab) => Object.entries(prefab?.parts || {}).map(([id, part]) => ({
    id,
    name: part.name || id,
}));

export const bindingsFor = (prefab, root) => {
    const bindings = {};
    Object.entries(prefab?.parts || {}).forEach(([id, part]) => {
        const target = part.pick?.(root);
        if (!target) return;
        const binding = { target };
        if (part.perspective != null) binding.perspective = part.perspective;
        bindings[id] = binding;
    });
    return bindings;
};

export const readDraft = (prefabId, clipName) => {
    const raw = readLibrary()[prefabId]?.[clipName];
    return raw ? normalizeClip(raw) : null;
};

export const hasDraft = (prefabId, clipName) => Boolean(readLibrary()[prefabId]?.[clipName]);

export const saveDraft = (prefabId, clipName, clip) => {
    const library = readLibrary();
    library[prefabId] = { ...(library[prefabId] || {}), [clipName]: normalizeClip(clip) };
    writeLibrary(library);
};

export const deleteDraft = (prefabId, clipName) => {
    const library = readLibrary();
    if (!library[prefabId]?.[clipName]) return;
    delete library[prefabId][clipName];
    writeLibrary(library);
};

export const clearDrafts = () => {
    const store = storage();
    if (!store) return;
    store.removeItem(LIBRARY_KEY);
    store.removeItem(LEGACY_DRAFT_KEY);
    store.removeItem(LEGACY_OVERRIDE_KEY);
};

export const listClipEntries = (prefab) => {
    if (!prefab) return [];
    const drafts = readLibrary()[prefab.id] || {};
    const names = prefab.clips.map((clip) => clip.name);
    Object.keys(drafts).forEach((name) => {
        if (!names.includes(name)) names.push(name);
    });
    return names.map((name) => ({
        name,
        stubbed: prefab.clips.some((clip) => clip.name === name),
        draft: Boolean(drafts[name]),
    }));
};

const clipKey = (prefab, clipName) => `${prefab?.id}:${clipName}`;

// A local draft wins. Otherwise use the file once it has been loaded, so playback can start on this frame.
const peekClip = (prefab, clipName) => (
    readDraft(prefab?.id, clipName) || clipCache.get(clipKey(prefab, clipName)) || null
);

export const preloadPrefabClips = async (prefab) => {
    await Promise.all((prefab?.clips || []).map(async (stub) => {
        const key = clipKey(prefab, stub.name);
        if (clipCache.has(key)) return;
        const loaded = await stub.load();
        clipCache.set(key, normalizeClip(loaded.default ?? loaded));
    }));
};

export const resolveClip = async (prefab, clipName) => {
    const ready = peekClip(prefab, clipName);
    if (ready) return ready;

    const stub = prefab?.clips.find((clip) => clip.name === clipName);
    if (!stub) return createEmptyClip(clipName);

    const loaded = await stub.load();
    const clip = normalizeClip(loaded.default ?? loaded);
    clipCache.set(clipKey(prefab, clipName), clip);
    return clip;
};

export const playPrefabClip = (prefab, clipName, root, options = {}) => {
    const { wait = true, ...playOptions } = options;
    const start = (clip) => {
        const timeline = playClip(clip, bindingsFor(prefab, root), playOptions);
        if (!wait) return { timeline };
        return timeline;
    };

    const ready = peekClip(prefab, clipName);
    if (ready) return start(ready);
    return resolveClip(prefab, clipName).then(start);
};
